import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  LayoutDashboard, FileText, BookOpen, CreditCard, Bell, User,
  Loader2, Clock, CheckCircle, XCircle, Settings, PlayCircle,
  AlertTriangle, Timer, Award, MessageCircle
} from 'lucide-react';

export default function StudentDashboard() {
  const { user, api } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [applications, setApplications] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [researchRooms, setResearchRooms] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileDialog, setProfileDialog] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(null);
  const [profileForm, setProfileForm] = useState({
    name: '', university: '', year_of_study: '', country: '',
    research_interests: '', skills: '', bio: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appsRes, pendingRes, coursesRes, payRes, notifRes, roomsRes, certRes] = await Promise.all([
        api.get('/applications'),
        api.get('/applications/pending-payment'),
        api.get('/my-courses'),
        api.get('/payments/history'),
        api.get('/notifications'),
        api.get('/research-rooms'),
        api.get('/certificates/my-certificates')
      ]);
      setApplications(appsRes.data.applications || []);
      setPendingPayments(pendingRes.data.applications || []);
      setMyCourses(coursesRes.data.courses || []);
      setPayments(payRes.data.transactions || []);
      setNotifications(notifRes.data.notifications || []);
      setResearchRooms(roomsRes.data.rooms || []);
      setCertificates(certRes.data.certificates || []);
    } catch (error) {
      console.error("Failed to fetch student dashboard data:", error);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '', university: user.profile?.university || '',
        year_of_study: user.profile?.year_of_study || '', country: user.profile?.country || '',
        research_interests: (user.profile?.research_interests || []).join(', '),
        skills: (user.profile?.skills || []).join(', '), bio: user.profile?.bio || ''
      });
    }
  }, [user]);

  const saveProfile = async () => {
    try {
      await api.put('/users/profile', {
        ...profileForm,
        research_interests: profileForm.research_interests.split(',').map(s => s.trim()).filter(Boolean),
        skills: profileForm.skills.split(',').map(s => s.trim()).filter(Boolean),
      });
      toast.success('Profile updated!');
      setProfileDialog(false);
    } catch { toast.error('Failed to update profile'); }
  };

  const handleResearchPayment = async (application) => {
    setPaymentLoading(application.application_id);
    try {
      const res = await api.post('/payments/create-checkout', {
        application_id: application.application_id,
        origin_url: window.location.origin
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create payment');
    } finally {
      setPaymentLoading(null);
    }
  };

  const statusColor = (s) => {
    if (s === 'enrolled' || s === 'accepted_by_mentor') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    if (s === 'verified') return 'bg-blue-50 text-blue-700 border-blue-100';
    if (s === 'payment_pending') return 'bg-purple-50 text-purple-700 border-purple-100';
    if (s === 'rejected_by_verification' || s === 'rejected_by_mentor' || s === 'rejected') return 'bg-red-50 text-red-700 border-red-100';
    if (s === 'under_verification' || s === 'submitted') return 'bg-amber-50 text-amber-700 border-amber-100';
    return 'bg-slate-50 text-slate-700 border-slate-100';
  };

  const formatStatus = (s) => {
    const statusMap = {
      'submitted': 'Submitted',
      'under_verification': 'Under Review',
      'verified': 'Verified - With Mentor',
      'rejected_by_verification': 'Not Verified',
      'accepted_by_mentor': 'Accepted',
      'rejected_by_mentor': 'Not Selected',
      'payment_pending': 'Payment Pending',
      'enrolled': 'Enrolled'
    };
    return statusMap[s] || s?.replace(/_/g, ' ');
  };

  const stats = [
    { label: 'Applications', value: applications.length, icon: <FileText className="h-5 w-5" /> },
    { label: 'Accepted', value: applications.filter(a => ['enrolled', 'accepted_by_mentor', 'payment_pending'].includes(a.status)).length, icon: <CheckCircle className="h-5 w-5" /> },
    { label: 'My Courses', value: myCourses.length, icon: <BookOpen className="h-5 w-5" /> },
    { label: 'Plan', value: user?.subscription?.plan === 'pro' ? 'Pro' : 'Free', icon: <CreditCard className="h-5 w-5" /> },
  ];

  return (
    <div className="page-container min-h-screen bg-slate-50" data-testid="student-dashboard">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Welcome, {user?.name}
            </h1>
            <p className="text-sm text-slate-500 mt-1">Student Dashboard</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/research')} className="text-slate-600" data-testid="research-projects-btn">
              <MessageCircle className="h-4 w-4 mr-2" /> Research Rooms
            </Button>
            <Button variant="outline" onClick={() => setProfileDialog(true)} className="text-slate-600" data-testid="edit-profile-btn">
              <Settings className="h-4 w-4 mr-2" /> Edit Profile
            </Button>
            {/* AI assistant temporarily disabled until OpenAI integration is added. */}
            {/*
            <Button onClick={() => setChatDialog(true)} className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white" data-testid="ai-chat-btn">
              <MessageSquare className="h-4 w-4 mr-2" /> AI Assistant
            </Button>
            */}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {stats.map((s) => (
            <div key={s.label} className="stat-card rounded-xl p-4 md:p-5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-[#1a5c5c]/10 flex items-center justify-center text-[#1a5c5c]">{s.icon}</div>
                <div>
                  <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{s.value}</p>
                  <p className="text-xs text-slate-500">{s.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="applications" data-testid="tab-applications">Applications</TabsTrigger>
            <TabsTrigger value="courses" data-testid="tab-courses">My Courses</TabsTrigger>
            <TabsTrigger value="certificates" data-testid="tab-certificates" className="flex items-center gap-2">
              <Award className="h-4 w-4" /> Certificates
            </TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="tab-notifications">
              Notifications {notifications.filter(n => !n.read).length > 0 && <span className="ml-1 h-4 w-4 rounded-full bg-red-500 text-white text-[10px] inline-flex items-center justify-center">{notifications.filter(n => !n.read).length}</span>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            {/* Pending Payments Alert */}
            {pendingPayments.length > 0 && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-amber-800 mb-2">Payment Required</h4>
                    <p className="text-sm text-amber-700 mb-3">You have accepted applications awaiting payment. Complete payment to secure your slot.</p>
                    <div className="space-y-2">
                      {pendingPayments.map((pp) => (
                        <div key={pp.application_id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100">
                          <div>
                            <p className="text-sm font-medium text-slate-900">{pp.project_details?.title || pp.project_title}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-slate-500">${pp.project_details?.price || 0}</span>
                              {pp.reservation?.expires_at && !pp.reservation?.expired && (
                                <span className="text-xs text-amber-600 flex items-center gap-1">
                                  <Timer className="h-3 w-3" />
                                  Expires: {new Date(pp.reservation.expires_at).toLocaleTimeString()}
                                </span>
                              )}
                              {pp.reservation?.expired && (
                                <Badge className="bg-red-100 text-red-700 text-xs">Reservation Expired</Badge>
                              )}
                            </div>
                          </div>
                          <Button
                            onClick={() => handleResearchPayment(pp)}
                            disabled={paymentLoading === pp.application_id || pp.reservation?.expired}
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            size="sm"
                            data-testid={`pay-btn-${pp.application_id}`}
                          >
                            {paymentLoading === pp.application_id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Pay Now'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope' }}>Recent Applications</h3>
              {applications.length === 0 ? (
                <p className="text-sm text-slate-500 py-4">No applications yet. <a href="/research" className="text-[#1a5c5c] underline">Browse research projects</a></p>
              ) : (
                <div className="space-y-3">
                  {applications.slice(0, 5).map((app) => (
                    <div key={app.application_id} className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{app.project_title}</p>
                        <p className="text-xs text-slate-500">Applied {new Date(app.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge className={`${statusColor(app.status)} border text-xs`}>{formatStatus(app.status)}</Badge>
                        {app.status === 'enrolled' && researchRooms.find(r => r.project_id === app.project_id) && (
                          <Button
                            size="sm"
                            onClick={() => navigate(`/research-room/${researchRooms.find(r => r.project_id === app.project_id).room_id}`)}
                            className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white h-8 text-xs px-3"
                          >
                            Enter Room
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="applications">
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope' }}>All Applications</h3>
              {loading ? <Loader2 className="h-6 w-6 animate-spin text-[#1a5c5c]" /> : applications.length === 0 ? (
                <p className="text-sm text-slate-500 py-4">No applications yet.</p>
              ) : (
                <div className="space-y-3">
                  {applications.map((app) => (
                    <div key={app.application_id} className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0" data-testid={`application-${app.application_id}`}>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">{app.project_title}</p>
                        <p className="text-xs text-slate-500">Applied {new Date(app.created_at).toLocaleDateString()}</p>
                        {app.motivation_letter && <p className="text-xs text-slate-400 mt-1 line-clamp-1">{app.motivation_letter}</p>}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Badge className={`${statusColor(app.status)} border text-xs`}>{formatStatus(app.status)}</Badge>
                        {app.status === 'enrolled' && researchRooms.find(r => r.project_id === app.project_id) && (
                          <Button
                            size="sm"
                            onClick={() => navigate(`/research-room/${researchRooms.find(r => r.project_id === app.project_id).room_id}`)}
                            className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white h-8 text-xs px-3"
                          >
                            Enter Room
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="courses">
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope' }}>My Courses</h3>
              {myCourses.length === 0 ? (
                <p className="text-sm text-slate-500 py-4">No courses yet. <a href="/courses" className="text-[#1a5c5c] underline">Browse courses</a></p>
              ) : (
                <div className="space-y-4">
                  {myCourses.map((c) => (
                    <div key={c.course_id} className="flex items-center gap-4 py-4 border-b border-slate-50 last:border-0" data-testid={`my-course-${c.course_id}`}>
                      <div className="h-16 w-24 rounded-lg bg-gradient-to-br from-[#1a5c5c]/10 to-[#1a5c5c]/5 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="h-6 w-6 text-[#1a5c5c]/50" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{c.course_title}</p>
                        <p className="text-xs text-slate-500">{c.course_instructor} • {c.course_duration}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Progress value={c.progress_percent || 0} className="flex-1 h-1.5" />
                          <span className="text-xs font-medium text-[#1a5c5c]">{c.progress_percent || 0}%</span>
                        </div>
                      </div>
                      {c.completed ? (
                        <div className="flex flex-col items-end gap-2 shrink-0">
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-100 flex-shrink-0">
                            <CheckCircle className="h-4 w-4 mr-1" /> Completed
                          </Badge>
                          <Button
                            onClick={() => setTab('certificates')}
                            variant="outline"
                            className="bg-white hover:bg-slate-50 text-slate-600 flex-shrink-0 border-slate-200"
                            size="sm"
                          >
                            <Award className="h-4 w-4 mr-1" /> View Certificate
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => navigate(`/courses/${c.course_id}`)}
                          className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white flex-shrink-0"
                          size="sm"
                        >
                          <PlayCircle className="h-4 w-4 mr-1" /> Continue
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="payments">
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope' }}>Payment History</h3>
              {payments.length === 0 ? (
                <p className="text-sm text-slate-500 py-4">No payments yet.</p>
              ) : (
                <div className="space-y-3">
                  {payments.map((p) => (
                    <div key={p.transaction_id} className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0">
                      <div>
                        <p className="text-sm font-medium text-slate-900 capitalize">{p.type} - ${p.amount}</p>
                        <p className="text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString()}</p>
                      </div>
                      <Badge className={`${p.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'} border text-xs capitalize`}>
                        {p.payment_status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="certificates">
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>My Certificates</h3>
                <Button
                  onClick={() => navigate('/certificates')}
                  className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white"
                  size="sm"
                >
                  <Award className="h-4 w-4 mr-2" /> View All
                </Button>
              </div>
              <p className="text-sm text-slate-600 mb-4">
                Certificates are earned upon successful course completion. Click "View All" to see and download your certificates.
              </p>

              {certificates.length === 0 ? (
                <div className="text-center py-8">
                  <Award className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">Complete courses to earn certificates</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {certificates.slice(0, 4).map((cert) => (
                    <div key={cert.certificate_id} className="p-4 border border-slate-100 rounded-lg bg-slate-50/50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-[#1a5c5c]/10 flex items-center justify-center">
                          <Award className="h-5 w-5 text-[#1a5c5c]" />
                        </div>
                        <div>
                          <p className="text-sm font-bold text-slate-900 line-clamp-1">{cert.course_title}</p>
                          <p className="text-xs text-slate-500">{new Date(cert.issue_date).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => navigate('/certificates')} className="text-slate-400 hover:text-[#1a5c5c]">
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="notifications">
            <div className="bg-white rounded-xl border border-slate-100 p-6">
              <h3 className="text-lg font-bold mb-4" style={{ fontFamily: 'Manrope' }}>Notifications</h3>
              {notifications.length === 0 ? (
                <p className="text-sm text-slate-500 py-4">No notifications.</p>
              ) : (
                <div className="space-y-3">
                  {notifications.map((n) => (
                    <div key={n.notification_id} className={`flex items-start gap-3 py-3 border-b border-slate-50 last:border-0 ${!n.read ? 'bg-[#1a5c5c]/5 -mx-3 px-3 rounded' : ''}`}
                      data-testid={`notification-${n.notification_id}`}>
                      <Bell className={`h-4 w-4 mt-0.5 ${!n.read ? 'text-[#1a5c5c]' : 'text-slate-400'}`} />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">{n.title}</p>
                        <p className="text-xs text-slate-500">{n.message}</p>
                        <p className="text-xs text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Profile Dialog */}
      <Dialog open={profileDialog} onOpenChange={setProfileDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>Edit Profile</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div><Label className="text-sm">Name</Label><Input value={profileForm.name} onChange={e => setProfileForm({ ...profileForm, name: e.target.value })} data-testid="profile-name" /></div>
            <div><Label className="text-sm">University</Label><Input value={profileForm.university} onChange={e => setProfileForm({ ...profileForm, university: e.target.value })} data-testid="profile-university" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-sm">Year of Study</Label><Input value={profileForm.year_of_study} onChange={e => setProfileForm({ ...profileForm, year_of_study: e.target.value })} data-testid="profile-year" /></div>
              <div><Label className="text-sm">Country</Label><Input value={profileForm.country} onChange={e => setProfileForm({ ...profileForm, country: e.target.value })} data-testid="profile-country" /></div>
            </div>
            <div><Label className="text-sm">Research Interests (comma separated)</Label><Input value={profileForm.research_interests} onChange={e => setProfileForm({ ...profileForm, research_interests: e.target.value })} placeholder="Cardiology, AI in Medicine" data-testid="profile-interests" /></div>
            <div><Label className="text-sm">Skills (comma separated)</Label><Input value={profileForm.skills} onChange={e => setProfileForm({ ...profileForm, skills: e.target.value })} placeholder="Python, SPSS, R" data-testid="profile-skills" /></div>
            <div><Label className="text-sm">Bio</Label><Textarea value={profileForm.bio} onChange={e => setProfileForm({ ...profileForm, bio: e.target.value })} rows={3} data-testid="profile-bio" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProfileDialog(false)}>Cancel</Button>
            <Button onClick={saveProfile} className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white" data-testid="save-profile-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI research assistant temporarily disabled until OpenAI integration is added. */}
      {/*
      <Dialog open={chatDialog} onOpenChange={setChatDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Manrope' }}>AI Research Assistant</DialogTitle></DialogHeader>
          <div className="h-80 overflow-y-auto space-y-3 p-2 bg-slate-50 rounded-lg">
            {chatHistory.length === 0 && <p className="text-sm text-slate-400 text-center mt-8">Ask me anything about medical research, the platform, or your applications!</p>}
            {chatHistory.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${msg.role === 'user' ? 'bg-[#1a5c5c] text-white' : 'bg-white border border-slate-200 text-slate-700'}`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {chatLoading && <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-lg px-3 py-2"><Loader2 className="h-4 w-4 animate-spin text-[#1a5c5c]" /></div></div>}
          </div>
          <div className="flex gap-2">
            <Input value={chatMessage} onChange={e => setChatMessage(e.target.value)} placeholder="Type your message..."
              onKeyDown={e => e.key === 'Enter' && sendChat()} data-testid="chat-input" />
            <Button onClick={sendChat} disabled={chatLoading} className="bg-[#1a5c5c] hover:bg-[#154a4a] text-white" data-testid="chat-send-btn">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      */}
    </div>
  );
}
